import React, { useState, useEffect } from "react"
import { useGuias } from "../context/GuiasContext"
import {Building2, Save, X, User, MapPin, Phone, Mail, FileText} from 'lucide-react'
import toast from "react-hot-toast"

interface CadastrarEmpresaProps {
  editingId?: string
  onSaveComplete?: () => void
}

const TIPOS_EMPRESA = ["MEI", "ME", "EPP", "LTDA", "S.A.", "EIRELI", "Autônomo", "Outro"]
const RAMOS = [
  "Alimentação e Bebidas", "Comércio Varejista", "Comércio Atacadista",
  "Prestação de Serviços", "Saúde e Beleza", "Educação e Cursos",
  "Construção Civil", "Transporte", "Tecnologia e Informática",
  "Agropecuária", "Indústria", "Entretenimento e Cultura",
  "Hotelaria e Turismo", "Oficina e Reparação", "Outros"
]

function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 14)
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
}

function formatCpf(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
}

function formatTelefone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11)
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3")
  }
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3")
}

function formatCep(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8)
  return digits.replace(/(\d{5})(\d{0,3})/, "$1-$2")
}

export default function CadastrarEmpresa({ editingId, onSaveComplete }: CadastrarEmpresaProps) {
  const { empresas, addEmpresa, updateEmpresa, getEmpresaById } = useGuias()
  const [loading, setLoading] = useState(false)

  const emptyForm = {
    razaoSocial: "", nomeFantasia: "", cnpj: "", cpf: "",
    cpfResponsavel: "", responsavel: "", tipoEmpresa: "MEI",
    ramoAtividade: "", cnae: "", inscricaoEstadual: "", inscricaoMunicipal: "",
    endereco: "", numero: "", complemento: "", bairro: "",
    cidade: "São José do Seridó", uf: "RN", cep: "",
    telefone: "", email: "", areaM2: "", status: "ativo", observacoes: ""
  }

  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    if (editingId) {
      const empresa = getEmpresaById(editingId)
      if (empresa) {
        setForm({
          razaoSocial: empresa.razaoSocial || "",
          nomeFantasia: empresa.nomeFantasia || "",
          cnpj: empresa.cnpj || "",
          cpf: empresa.cpf || "",
          cpfResponsavel: empresa.cpfResponsavel || "",
          responsavel: empresa.responsavel || "",
          tipoEmpresa: empresa.tipoEmpresa || "MEI",
          ramoAtividade: empresa.ramoAtividade || "",
          cnae: empresa.cnae || "",
          inscricaoEstadual: empresa.inscricaoEstadual || "",
          inscricaoMunicipal: empresa.inscricaoMunicipal || "",
          endereco: empresa.endereco || "",
          numero: empresa.numero || "",
          complemento: empresa.complemento || "",
          bairro: empresa.bairro || "",
          cidade: empresa.cidade || "São José do Seridó",
          uf: empresa.uf || "RN",
          cep: empresa.cep || "",
          telefone: empresa.telefone || "",
          email: empresa.email || "",
          areaM2: empresa.areaM2 || "",
          status: empresa.status || "ativo",
          observacoes: empresa.observacoes || ""
        })
      }
    }
  }, [editingId, empresas])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    let formatted = value
    if (name === "cnpj") formatted = formatCnpj(value)
    else if (name === "cpf" || name === "cpfResponsavel") formatted = formatCpf(value)
    else if (name === "telefone") formatted = formatTelefone(value)
    else if (name === "cep") formatted = formatCep(value)
    setForm(prev => ({ ...prev, [name]: formatted }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.razaoSocial.trim()) { toast.error("Razão social é obrigatória"); return }
    if (!form.cnpj.trim()) { toast.error("CNPJ é obrigatório"); return }

    setLoading(true)
    try {
      if (editingId) {
        await updateEmpresa(editingId, form)
      } else {
        await addEmpresa(form)
        setForm(emptyForm)
      }
      onSaveComplete?.()
    } finally {
      setLoading(false)
    }
  }

  const inputClass = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004161]/30 focus:border-[#004161] bg-white"
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1"

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-[#004161] px-6 py-4 flex items-center gap-3">
        <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-white font-bold text-base">
            {editingId ? "Editar Empresa" : "Cadastrar Empresa"}
          </h2>
          <p className="text-blue-200 text-xs">Dados do estabelecimento comercial</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">

        {/* Identificação */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-[#004161]" />
            <h3 className="text-sm font-bold text-[#004161] uppercase tracking-wide">Identificação</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Razão Social / Nome Completo *</label>
              <input name="razaoSocial" value={form.razaoSocial} onChange={handleChange} className={inputClass} placeholder="Razão social ou nome completo" required />
            </div>
            <div>
              <label className={labelClass}>Nome Fantasia</label>
              <input name="nomeFantasia" value={form.nomeFantasia} onChange={handleChange} className={inputClass} placeholder="Nome fantasia" />
            </div>
            <div>
              <label className={labelClass}>CNPJ *</label>
              <input name="cnpj" value={form.cnpj} onChange={handleChange} className={inputClass} placeholder="00.000.000/0000-00" required />
            </div>
            <div>
              <label className={labelClass}>CPF (Autônomo/MEI)</label>
              <input name="cpf" value={form.cpf} onChange={handleChange} className={inputClass} placeholder="000.000.000-00" />
            </div>
            <div>
              <label className={labelClass}>Tipo de Empresa</label>
              <select name="tipoEmpresa" value={form.tipoEmpresa} onChange={handleChange} className={inputClass}>
                {TIPOS_EMPRESA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Inscrição Municipal</label>
              <input name="inscricaoMunicipal" value={form.inscricaoMunicipal} onChange={handleChange} className={inputClass} placeholder="Nº inscrição municipal" />
            </div>
            <div>
              <label className={labelClass}>Inscrição Estadual</label>
              <input name="inscricaoEstadual" value={form.inscricaoEstadual} onChange={handleChange} className={inputClass} placeholder="Nº inscrição estadual" />
            </div>
            <div>
              <label className={labelClass}>CNAE</label>
              <input name="cnae" value={form.cnae} onChange={handleChange} className={inputClass} placeholder="0000-0/00" />
            </div>
          </div>
        </div>

        {/* Atividade */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-[#004161]" />
            <h3 className="text-sm font-bold text-[#004161] uppercase tracking-wide">Atividade</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Ramo de Atividade</label>
              <select name="ramoAtividade" value={form.ramoAtividade} onChange={handleChange} className={inputClass}>
                <option value="">Selecione...</option>
                {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Área do Estabelecimento (m²)</label>
              <input name="areaM2" value={form.areaM2} onChange={handleChange} className={inputClass} placeholder="Ex: 45" />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select name="status" value={form.status} onChange={handleChange} className={inputClass}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="suspenso">Suspenso</option>
              </select>
            </div>
          </div>
        </div>

        {/* Responsável */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-[#004161]" />
            <h3 className="text-sm font-bold text-[#004161] uppercase tracking-wide">Responsável Legal</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Nome do Responsável</label>
              <input name="responsavel" value={form.responsavel} onChange={handleChange} className={inputClass} placeholder="Nome completo do responsável" />
            </div>
            <div>
              <label className={labelClass}>CPF do Responsável</label>
              <input name="cpfResponsavel" value={form.cpfResponsavel} onChange={handleChange} className={inputClass} placeholder="000.000.000-00" />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-[#004161]" />
            <h3 className="text-sm font-bold text-[#004161] uppercase tracking-wide">Endereço</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Logradouro</label>
              <input name="endereco" value={form.endereco} onChange={handleChange} className={inputClass} placeholder="Rua, Avenida, Praça..." />
            </div>
            <div>
              <label className={labelClass}>Número</label>
              <input name="numero" value={form.numero} onChange={handleChange} className={inputClass} placeholder="Nº" />
            </div>
            <div>
              <label className={labelClass}>Complemento</label>
              <input name="complemento" value={form.complemento} onChange={handleChange} className={inputClass} placeholder="Sala, Loja..." />
            </div>
            <div>
              <label className={labelClass}>Bairro</label>
              <input name="bairro" value={form.bairro} onChange={handleChange} className={inputClass} placeholder="Bairro" />
            </div>
            <div>
              <label className={labelClass}>Cidade</label>
              <input name="cidade" value={form.cidade} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>UF</label>
              <input name="uf" value={form.uf} onChange={handleChange} className={inputClass} maxLength={2} />
            </div>
            <div>
              <label className={labelClass}>CEP</label>
              <input name="cep" value={form.cep} onChange={handleChange} className={inputClass} placeholder="00000-000" />
            </div>
          </div>
        </div>

        {/* Contato */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Phone className="w-4 h-4 text-[#004161]" />
            <h3 className="text-sm font-bold text-[#004161] uppercase tracking-wide">Contato</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Telefone</label>
              <input name="telefone" value={form.telefone} onChange={handleChange} className={inputClass} placeholder="(84) 00000-0000" />
            </div>
            <div>
              <label className={labelClass}>E-mail</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} placeholder="empresa@email.com" />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div>
          <label className={labelClass}>Observações</label>
          <textarea name="observacoes" value={form.observacoes} onChange={handleChange} rows={3} className={inputClass + " resize-none"} placeholder="Informações adicionais..." />
        </div>

        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          {onSaveComplete && (
            <button type="button" onClick={onSaveComplete}
              className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors text-sm font-medium">
              <X className="w-4 h-4" /> Cancelar
            </button>
          )}
          <button type="submit" disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-[#004161] text-white rounded-xl hover:bg-[#003050] transition-colors text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" />
            {loading ? "Salvando..." : editingId ? "Atualizar Empresa" : "Cadastrar Empresa"}
          </button>
        </div>
      </form>
    </div>
  )
}
